import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaUser, FaSpinner, FaChartLine, FaShieldAlt, FaPiggyBank, FaFileAlt, FaMoneyBillWave, FaCreditCard, FaWallet, FaClipboardList, FaLightbulb, FaFilePdf, FaUserCircle, FaHome, FaCar, FaGraduationCap, FaBullseye, FaChild, FaHeart, FaUmbrellaBeach, FaGlobe, FaCheckCircle, FaTimesCircle, FaPhone, FaEnvelope, FaTimes, FaPlus, FaEdit, FaCalendar, FaDownload } from 'react-icons/fa';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import RiskProfiler from '../components/RiskProfiler';
import InvestmentTracker from '../components/InvestmentTracker';
import LiabilityTracker from '../components/LiabilityTracker';
import InsuranceAnalysis from '../components/InsuranceAnalysis';
import GoalPlanning from '../components/GoalPlanning';
import ExecutiveSummary from '../components/ExecutiveSummary';
import NetWorthSnapshot from '../components/NetWorthSnapshot';
import NextStepsFeedback from '../components/NextStepsFeedback';
import PersonalFinanceWisdom from '../components/PersonalFinanceWisdom';
import PDFReportPreview from '../components/PDFReportPreview';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientPlanView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('clientdata');
  const [showPDFPreview, setShowPDFPreview] = useState(false);
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [finalizeMessage, setFinalizeMessage] = useState(null);
  
  // Check if current user is an advisor (not a client)
  const isAdvisor = user?.role === 'advisor' || user?.role === 'manager' || user?.role === 'super_admin';
  
  // Finalize Report Form State
  const [finalizeData, setFinalizeData] = useState({
    next_review_date: '',
    key_action_items: [''],
    advisor_notes: ''
  });

  useEffect(() => {
    fetchClient();
    // Set default tab based on user role - clients don't see 'clientdata' tab
    if (user?.role === 'client') {
      setActiveTab('profile');
    }
  }, [id, user?.role]);

  const fetchClient = async () => {
    try {
      const response = await axios.get(`${API}/clients/${id}`);
      setClient(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const tabs = [
    { id: 'clientdata', label: 'Client Submitted Data', icon: FaUserCircle, advisorOnly: true },
    { id: 'profile', label: 'Profile', icon: FaUser },
    { id: 'risk', label: 'Risk Profiler', icon: FaChartLine },
    { id: 'investments', label: 'Investments', icon: FaMoneyBillWave },
    { id: 'liabilities', label: 'Liabilities', icon: FaCreditCard },
    { id: 'insurance', label: 'Insurance', icon: FaShieldAlt },
    { id: 'goals', label: 'Goals', icon: FaPiggyBank },
    { id: 'networth', label: 'Net Worth', icon: FaWallet },
    { id: 'summary', label: 'Summary', icon: FaFileAlt },
    { id: 'wisdom', label: 'Wisdom', icon: FaLightbulb },
    { id: 'nextsteps', label: 'Next Steps', icon: FaClipboardList }
  ];
  
  // Filter tabs based on user role
  const visibleTabs = tabs.filter(tab => !tab.advisorOnly || isAdvisor);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <FaSpinner className="animate-spin text-[#FFD700]" size={48} />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400 text-lg">Client not found</p>
      </div>
    );
  }

  // Calculate totals from client submitted data
  const totalAssets = (
    (parseInt(client.savings_account) || 0) +
    (parseInt(client.fixed_deposits) || 0) +
    (parseInt(client.mutual_funds) || 0) +
    (parseInt(client.stocks) || 0) +
    (parseInt(client.ppf_epf) || 0) +
    (parseInt(client.real_estate_value) || 0) +
    (parseInt(client.gold_jewelry) || 0) +
    (parseInt(client.other_assets) || 0)
  );

  const totalLiabilities = (
    (parseInt(client.home_loan) || 0) +
    (parseInt(client.car_loan) || 0) +
    (parseInt(client.personal_loan) || 0) +
    (parseInt(client.education_loan) || 0) +
    (parseInt(client.credit_card_outstanding) || 0) +
    (parseInt(client.other_liabilities) || 0)
  );

  const totalInsuranceCover = (
    (parseInt(client.life_insurance_cover) || 0) +
    (parseInt(client.term_insurance_cover) || 0)
  );

  // Function to copy client submitted data to Arth Gurukul form
  const handleCopyToArthKundli = () => {
    // Map client submitted data to Arth Gurukul form fields
    const arthKundliData = {
      // Personal Information
      name: client.name || '',
      profile_occupation: client.occupation || client.profile_occupation || '',
      date_of_birth: client.date_of_birth || '',
      age: client.age || '',
      contact: client.phone || client.contact || '',
      
      // Family Information
      spouse_name: client.spouse_name || '',
      total_dependents: client.total_dependents || 0,
      children: (client.children || []).map(child => ({
        name: child.name || '',
        date_of_birth: child.date_of_birth || '',
        age: child.age || '',
        class_grade: child.class_grade || '',
        education_goal: child.education_goal || '',
        marriage_goal: child.marriage_goal || ''
      })),
      
      // Pre-fill flag to indicate data came from client submission
      prefilled_from_client: true,
      prefill_timestamp: new Date().toISOString(),
      
      // Additional client data for reference (advisor can review)
      client_submitted_data: {
        // Income
        annual_income: client.annual_income,
        monthly_income: client.monthly_income,
        other_income: client.other_income,
        rental_income: client.rental_income,
        
        // Expenses
        monthly_expenses: client.monthly_expenses,
        rent_emi: client.rent_emi,
        
        // Assets
        savings_account: client.savings_account,
        fixed_deposits: client.fixed_deposits,
        mutual_funds: client.mutual_funds,
        stocks: client.stocks,
        ppf_epf: client.ppf_epf,
        real_estate_value: client.real_estate_value,
        gold_jewelry: client.gold_jewelry,
        
        // Liabilities
        home_loan: client.home_loan,
        home_loan_emi: client.home_loan_emi,
        home_loan_protected: client.home_loan_protected,
        car_loan: client.car_loan,
        car_loan_emi: client.car_loan_emi,
        car_loan_protected: client.car_loan_protected,
        personal_loan: client.personal_loan,
        education_loan: client.education_loan,
        credit_card_outstanding: client.credit_card_outstanding,
        
        // Insurance
        life_insurance_cover: client.life_insurance_cover,
        life_insurance_premium: client.life_insurance_premium,
        term_insurance_cover: client.term_insurance_cover,
        term_insurance_premium: client.term_insurance_premium,
        health_insurance_cover: client.health_insurance_cover,
        health_insurance_premium: client.health_insurance_premium,
        
        // Goals
        retirement_age: client.retirement_age,
        retirement_monthly_expense: client.retirement_monthly_expense,
        child_goals: client.child_goals,
        other_goals: client.other_goals,
        home_purchase_goal: client.home_purchase_goal,
        vacation_fund: client.vacation_fund,
        emergency_fund_target: client.emergency_fund_target,
        
        // Risk Profile
        risk_tolerance: client.risk_tolerance,
        investment_experience: client.investment_experience,
        investment_horizon: client.investment_horizon,
        
        // Calculated Totals
        total_assets: totalAssets,
        total_liabilities: totalLiabilities,
        net_worth: totalAssets - totalLiabilities,
        total_insurance_cover: totalInsuranceCover
      }
    };
    
    // Store in localStorage for the form to pick up
    localStorage.setItem('arthKundliPrefillData', JSON.stringify(arthKundliData));
    
    // Navigate to edit page
    navigate(`/dashboard/clients/${id}/edit`);
  };

  // Handle Finalize Report
  const handleFinalizeReport = async () => {
    setFinalizing(true);
    setFinalizeMessage(null);
    
    try {
      // Collect all the data needed for the finalized report
      const reportData = {
        // Financial Overview
        net_worth: totalAssets - totalLiabilities,
        total_investments: totalAssets,
        total_liabilities: totalLiabilities,
        
        // Life Insurance Analysis
        life_insurance_required: (parseInt(client.annual_income) || 0) * 10, // Rule of thumb: 10x annual income
        life_insurance_current: totalInsuranceCover,
        life_insurance_gap: Math.max(0, ((parseInt(client.annual_income) || 0) * 10) - totalInsuranceCover),
        
        // Health Insurance Analysis
        health_insurance_required: (parseInt(client.annual_income) || 0) * 2, // Rule: 2x annual income
        health_insurance_current: parseInt(client.health_insurance_cover) || 0,
        health_insurance_gap: Math.max(0, ((parseInt(client.annual_income) || 0) * 2) - (parseInt(client.health_insurance_cover) || 0)),
        
        // Emergency Fund
        emergency_fund_required: (parseInt(client.monthly_expenses) || 0) * 6, // 6 months expenses
        emergency_fund_current: parseInt(client.savings_account) || 0,
        emergency_fund_gap: Math.max(0, ((parseInt(client.monthly_expenses) || 0) * 6) - (parseInt(client.savings_account) || 0)),
        
        // Goals Summary - Combine child_goals and other_goals
        goals_summary: [
          ...(client.child_goals || []).map(g => ({
            goal_name: g.child_name ? `${g.child_name}'s ${g.goal_type}` : `Child ${g.goal_type}`,
            goal_type: g.goal_type === 'marriage' ? 'marriage' : 'education',
            target_amount: parseFloat(g.target_amount) || 0,
            current_progress: parseFloat(g.current_savings) || 0,
            gap: Math.max(0, (parseFloat(g.target_amount) || 0) - (parseFloat(g.current_savings) || 0)),
            monthly_sip_required: calculateSIP(parseFloat(g.target_amount) || 0, parseInt(g.target_year) || new Date().getFullYear() + 10, parseFloat(g.current_savings) || 0),
            progress_percentage: ((parseFloat(g.current_savings) || 0) / (parseFloat(g.target_amount) || 1)) * 100
          })),
          ...(client.other_goals || []).map(g => ({
            goal_name: g.goal_name || 'Custom Goal',
            goal_type: 'custom',
            target_amount: parseFloat(g.target_amount) || 0,
            current_progress: parseFloat(g.current_savings) || 0,
            gap: Math.max(0, (parseFloat(g.target_amount) || 0) - (parseFloat(g.current_savings) || 0)),
            monthly_sip_required: calculateSIP(parseFloat(g.target_amount) || 0, parseInt(g.target_year) || new Date().getFullYear() + 5, parseFloat(g.current_savings) || 0),
            progress_percentage: ((parseFloat(g.current_savings) || 0) / (parseFloat(g.target_amount) || 1)) * 100
          })),
          // Add retirement goal if retirement data exists
          client.retirement_monthly_expense ? {
            goal_name: 'Retirement Corpus',
            goal_type: 'retirement',
            target_amount: (parseInt(client.retirement_monthly_expense) || 0) * 12 * 25, // 25 years of expenses
            current_progress: parseInt(client.ppf_epf) || 0,
            gap: Math.max(0, ((parseInt(client.retirement_monthly_expense) || 0) * 12 * 25) - (parseInt(client.ppf_epf) || 0)),
            monthly_sip_required: calculateSIP((parseInt(client.retirement_monthly_expense) || 0) * 12 * 25, (parseInt(client.retirement_age) || 60) - (parseInt(client.age) || 30) + new Date().getFullYear(), parseInt(client.ppf_epf) || 0),
            progress_percentage: ((parseInt(client.ppf_epf) || 0) / (((parseInt(client.retirement_monthly_expense) || 0) * 12 * 25) || 1)) * 100
          } : null
        ].filter(Boolean),
        
        // Calculate total monthly commitment
        total_monthly_commitment: 0, // Will be calculated below
        
        // Next Steps from form
        next_review_date: finalizeData.next_review_date || null,
        key_action_items: finalizeData.key_action_items.filter(item => item.trim() !== ''),
        advisor_notes: finalizeData.advisor_notes || ''
      };
      
      // Calculate total monthly commitment
      reportData.total_monthly_commitment = reportData.goals_summary.reduce((sum, g) => sum + (g.monthly_sip_required || 0), 0);
      
      // Send to backend
      await axios.post(`${API}/advisor/finalize-report/${id}`, reportData);
      
      setFinalizeMessage({ type: 'success', text: 'Report finalized successfully! Client can now view their financial summary.' });
      setShowFinalizeModal(false);
      
      // Refresh client data
      fetchClient();
      
    } catch (error) {
      setFinalizeMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to finalize report' });
    } finally {
      setFinalizing(false);
    }
  };
  
  // Helper function to calculate SIP required
  const calculateSIP = (targetAmount, targetYear, existingCorpus) => {
    const yearsRemaining = Math.max(1, targetYear - new Date().getFullYear());
    const rate = 0.12 / 12; // 12% annual return, monthly
    const n = yearsRemaining * 12;
    const fvExisting = existingCorpus * Math.pow(1 + rate, n);
    const amountNeeded = targetAmount - fvExisting;
    if (amountNeeded <= 0) return 0;
    const sip = (amountNeeded * rate) / (Math.pow(1 + rate, n) - 1);
    return Math.round(sip);
  };
  
  // Add action item handler
  const addActionItem = () => {
    setFinalizeData(prev => ({
      ...prev,
      key_action_items: [...prev.key_action_items, '']
    }));
  };
  
  const removeActionItem = (index) => {
    setFinalizeData(prev => ({
      ...prev,
      key_action_items: prev.key_action_items.filter((_, i) => i !== index)
    }));
  };
  
  const updateActionItem = (index, value) => {
    setFinalizeData(prev => ({
      ...prev,
      key_action_items: prev.key_action_items.map((item, i) => i === index ? value : item)
    }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/dashboard')}
          className="p-3 bg-[#1E3A5F] text-white rounded-lg hover:bg-[#2D5A8F] transition-all"
        >
          <FaArrowLeft />
        </button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-white">{client.name}</h1>
          <p className="text-gray-400">
            {client.profile_occupation || client.occupation} {client.age && `• ${client.age} years`}
          </p>
          {client.profile_status && (
            <span className={`mt-2 inline-block px-3 py-1 rounded-full text-xs font-medium ${
              client.profile_status === 'submitted' ? 'bg-yellow-500/20 text-yellow-400' :
              client.profile_status === 'report_access_granted' ? 'bg-green-500/20 text-green-400' :
              client.profile_status === 'download_allowed' ? 'bg-blue-500/20 text-blue-400' :
              'bg-gray-500/20 text-gray-400'
            }`}>
              {client.profile_status === 'submitted' ? 'Pending Review' :
               client.profile_status === 'report_access_granted' ? 'Report Access Granted' :
               client.profile_status === 'download_allowed' ? 'Download Enabled' :
               client.profile_status}
            </span>
          )}
        </div>
        <button
          onClick={() => setShowPDFPreview(true)}
          className="px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white font-bold rounded-lg hover:from-red-700 hover:to-red-800 transition-all flex items-center space-x-2 mr-2"
          data-testid="download-pdf-btn"
        >
          <FaFilePdf />
          <span>{isAdvisor ? 'Preview Report' : 'View Full Report'}</span>
        </button>
        
        {/* Advisor-only buttons */}
        {isAdvisor && (
          <>
            <button
              onClick={handleCopyToArthKundli}
              className="px-6 py-3 bg-gradient-to-r from-green-600 to-green-500 text-white font-bold rounded-lg hover:from-green-500 hover:to-green-400 transition-all flex items-center space-x-2 mr-2"
              data-testid="copy-to-arth-kundli-btn"
            >
              <FaClipboardList />
              <span>Copy to Arth Gurukul</span>
            </button>
            <button
              onClick={() => navigate(`/dashboard/clients/${id}/edit`)}
              className="px-6 py-3 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all mr-2"
            >
              Edit Arth Gurukul
            </button>
            <button
              onClick={() => setShowFinalizeModal(true)}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-500 text-white font-bold rounded-lg hover:from-purple-500 hover:to-purple-400 transition-all flex items-center space-x-2"
              data-testid="finalize-report-btn"
            >
              <FaCheckCircle />
              <span>Finalize Report</span>
            </button>
          </>
        )}
        
        {/* Client-only: Download PDF button */}
        {!isAdvisor && (
          <button
            onClick={() => setShowPDFPreview(true)}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white font-bold rounded-lg hover:from-blue-500 hover:to-blue-400 transition-all flex items-center space-x-2"
            data-testid="download-report-client-btn"
          >
            <FaDownload />
            <span>Download PDF</span>
          </button>
        )}
      </div>
      
      {/* Success/Error Message */}
      {finalizeMessage && (
        <div className={`p-4 rounded-lg ${
          finalizeMessage.type === 'success' ? 'bg-green-500/20 border border-green-500 text-green-400' : 'bg-red-500/20 border border-red-500 text-red-400'
        }`}>
          {finalizeMessage.text}
        </div>
      )}

      {/* Tabs */}
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl border border-[#2D5A8F] overflow-hidden">
        <div className="flex overflow-x-auto scrollbar-hide">
          {visibleTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center justify-center space-x-1.5 px-3 py-3 font-medium transition-all border-b-2 whitespace-nowrap min-w-fit ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] border-[#FFD700]'
                    : 'text-gray-400 hover:text-white hover:bg-[#1E3A5F] border-transparent'
                }`}
              >
                <Icon size={14} />
                <span className="text-sm">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div>
        {/* CLIENT SUBMITTED DATA TAB */}
        {activeTab === 'clientdata' && (
          <div className="space-y-6" data-testid="client-submitted-data">
            {/* Quick Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-green-900/30 to-green-800/30 rounded-xl p-4 border border-green-500/50">
                <p className="text-gray-400 text-sm">Total Assets</p>
                <p className="text-2xl font-bold text-green-400">{formatCurrency(totalAssets)}</p>
              </div>
              <div className="bg-gradient-to-br from-red-900/30 to-red-800/30 rounded-xl p-4 border border-red-500/50">
                <p className="text-gray-400 text-sm">Total Liabilities</p>
                <p className="text-2xl font-bold text-red-400">{formatCurrency(totalLiabilities)}</p>
              </div>
              <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-4 border border-[#FFD700]/50">
                <p className="text-gray-400 text-sm">Net Worth</p>
                <p className="text-2xl font-bold text-[#FFD700]">{formatCurrency(totalAssets - totalLiabilities)}</p>
              </div>
              <div className="bg-gradient-to-br from-blue-900/30 to-blue-800/30 rounded-xl p-4 border border-blue-500/50">
                <p className="text-gray-400 text-sm">Life Insurance Cover</p>
                <p className="text-2xl font-bold text-blue-400">{formatCurrency(totalInsuranceCover)}</p>
              </div>
            </div>

            {/* Header Banner */}
            <div className="bg-gradient-to-r from-[#FFD700]/20 to-[#D4AF37]/20 rounded-xl p-4 border border-[#FFD700]/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <FaUserCircle className="text-[#FFD700] text-2xl" />
                  <div>
                    <h2 className="text-lg font-bold text-[#FFD700]">Client Submitted Data</h2>
                    <p className="text-gray-400 text-sm">This is the financial data submitted by the client for review</p>
                  </div>
                </div>
                {client.submitted_at && (
                  <span className="text-gray-400 text-sm">
                    Submitted: {new Date(client.submitted_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>

            {/* Personal Information */}
            <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
              <h3 className="text-xl font-bold text-[#FFD700] mb-4 flex items-center">
                <FaUser className="mr-2" /> Personal Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <DataField label="Full Name" value={client.name} />
                <DataField label="Email" value={client.email} />
                <DataField label="Phone" value={client.phone || client.contact} />
                <DataField label="Date of Birth" value={client.date_of_birth} />
                <DataField label="Age" value={client.age ? `${client.age} years` : null} />
                <DataField label="PAN Number" value={client.pan_number} />
                <DataField label="Occupation" value={client.occupation || client.profile_occupation} />
                <DataField label="Address" value={client.address} span={2} />
              </div>
              
              {/* Family Info */}
              <div className="mt-6 pt-4 border-t border-[#2D5A8F]">
                <h4 className="text-lg font-medium text-white mb-3 flex items-center">
                  <FaHeart className="mr-2 text-red-400" /> Family Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <DataField label="Spouse Name" value={client.spouse_name} />
                  <DataField label="Spouse DOB" value={client.spouse_dob} />
                  <DataField label="Spouse Occupation" value={client.spouse_occupation} />
                  <DataField label="Total Dependents" value={client.total_dependents} />
                </div>
              </div>

              {/* Children */}
              {client.children && client.children.length > 0 && (
                <div className="mt-6 pt-4 border-t border-[#2D5A8F]">
                  <h4 className="text-lg font-medium text-white mb-3 flex items-center">
                    <FaChild className="mr-2 text-blue-400" /> Children
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {client.children.map((child, idx) => (
                      <div key={idx} className="bg-[#0F172A] rounded-lg p-3 border border-[#2D5A8F]">
                        <p className="text-white font-medium">{child.name || `Child ${idx + 1}`}</p>
                        <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                          {child.age && <p className="text-gray-400">Age: <span className="text-white">{child.age}</span></p>}
                          {child.education_goal && <p className="text-gray-400">Education Goal: <span className="text-green-400">{formatCurrency(child.education_goal)}</span></p>}
                          {child.marriage_goal && <p className="text-gray-400">Marriage Goal: <span className="text-pink-400">{formatCurrency(child.marriage_goal)}</span></p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Income & Expenses */}
            <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
              <h3 className="text-xl font-bold text-[#FFD700] mb-4 flex items-center">
                <FaMoneyBillWave className="mr-2" /> Income & Expenses
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Income */}
                <div>
                  <h4 className="text-lg font-medium text-green-400 mb-3">Income</h4>
                  <div className="space-y-2">
                    <DataField label="Annual Income" value={formatCurrency(client.annual_income)} highlight="green" />
                    <DataField label="Monthly Income" value={formatCurrency(client.monthly_income)} highlight="green" />
                    <DataField label="Other Income" value={formatCurrency(client.other_income)} />
                    <DataField label="Rental Income" value={formatCurrency(client.rental_income)} />
                    <DataField label="Interest Income" value={formatCurrency(client.interest_income)} />
                    <DataField label="Dividend Income" value={formatCurrency(client.dividend_income)} />
                  </div>
                </div>
                {/* Expenses */}
                <div>
                  <h4 className="text-lg font-medium text-red-400 mb-3">Monthly Expenses</h4>
                  <div className="space-y-2">
                    <DataField label="Total Monthly Expenses" value={formatCurrency(client.monthly_expenses)} highlight="red" />
                    <DataField label="Rent/EMI" value={formatCurrency(client.rent_emi)} />
                    <DataField label="Utilities" value={formatCurrency(client.utilities)} />
                    <DataField label="Education Expenses" value={formatCurrency(client.education_expenses)} />
                    <DataField label="Medical Expenses" value={formatCurrency(client.medical_expenses)} />
                    <DataField label="Lifestyle Expenses" value={formatCurrency(client.lifestyle_expenses)} />
                  </div>
                </div>
              </div>
            </div>

            {/* Assets */}
            <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
              <h3 className="text-xl font-bold text-[#FFD700] mb-4 flex items-center">
                <FaPiggyBank className="mr-2" /> Assets
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <DataField label="Savings Account" value={formatCurrency(client.savings_account)} />
                <DataField label="Fixed Deposits" value={formatCurrency(client.fixed_deposits)} />
                <DataField label="Mutual Funds" value={formatCurrency(client.mutual_funds)} />
                <DataField label="Stocks" value={formatCurrency(client.stocks)} />
                <DataField label="PPF/EPF" value={formatCurrency(client.ppf_epf)} />
                <DataField label="Real Estate" value={formatCurrency(client.real_estate_value)} />
                <DataField label="Gold/Jewelry" value={formatCurrency(client.gold_jewelry)} />
                <DataField label="Other Assets" value={formatCurrency(client.other_assets)} />
              </div>
              <div className="mt-4 pt-4 border-t border-[#2D5A8F] flex justify-end">
                <div className="text-right">
                  <p className="text-gray-400 text-sm">Total Assets</p>
                  <p className="text-2xl font-bold text-green-400">{formatCurrency(totalAssets)}</p>
                </div>
              </div>
            </div>

            {/* Liabilities */}
            <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
              <h3 className="text-xl font-bold text-[#FFD700] mb-4 flex items-center">
                <FaHome className="mr-2" /> Liabilities
              </h3>
              <div className="space-y-4">
                {/* Home Loan */}
                <LoanRow 
                  icon={FaHome}
                  label="Home Loan"
                  outstanding={client.home_loan}
                  emi={client.home_loan_emi}
                  isProtected={client.home_loan_protected}
                  formatCurrency={formatCurrency}
                />
                {/* Car Loan */}
                <LoanRow 
                  icon={FaCar}
                  label="Car Loan"
                  outstanding={client.car_loan}
                  emi={client.car_loan_emi}
                  isProtected={client.car_loan_protected}
                  formatCurrency={formatCurrency}
                />
                {/* Personal Loan */}
                <LoanRow 
                  icon={FaWallet}
                  label="Personal Loan"
                  outstanding={client.personal_loan}
                  emi={client.personal_loan_emi}
                  isProtected={client.personal_loan_protected}
                  formatCurrency={formatCurrency}
                />
                {/* Education Loan */}
                <LoanRow 
                  icon={FaGraduationCap}
                  label="Education Loan"
                  outstanding={client.education_loan}
                  emi={client.education_loan_emi}
                  isProtected={client.education_loan_protected}
                  formatCurrency={formatCurrency}
                />
                {/* Other */}
                <div className="grid grid-cols-2 gap-4">
                  <DataField label="Credit Card Outstanding" value={formatCurrency(client.credit_card_outstanding)} />
                  <DataField label="Other Liabilities" value={formatCurrency(client.other_liabilities)} />
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-[#2D5A8F] flex justify-end">
                <div className="text-right">
                  <p className="text-gray-400 text-sm">Total Liabilities</p>
                  <p className="text-2xl font-bold text-red-400">{formatCurrency(totalLiabilities)}</p>
                </div>
              </div>
            </div>

            {/* Insurance */}
            <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
              <h3 className="text-xl font-bold text-[#FFD700] mb-4 flex items-center">
                <FaShieldAlt className="mr-2" /> Insurance Coverage
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <InsuranceCard 
                  label="Life Insurance"
                  cover={client.life_insurance_cover}
                  premium={client.life_insurance_premium}
                  formatCurrency={formatCurrency}
                  color="red"
                />
                <InsuranceCard 
                  label="Term Insurance"
                  cover={client.term_insurance_cover}
                  premium={client.term_insurance_premium}
                  formatCurrency={formatCurrency}
                  color="green"
                />
                <InsuranceCard 
                  label="Health Insurance"
                  cover={client.health_insurance_cover}
                  premium={client.health_insurance_premium}
                  formatCurrency={formatCurrency}
                  color="blue"
                />
              </div>
              {client.vehicle_insurance && (
                <div className="mt-4">
                  <DataField label="Vehicle Insurance Premium (Annual)" value={formatCurrency(client.vehicle_insurance)} />
                </div>
              )}
            </div>

            {/* Financial Goals */}
            <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
              <h3 className="text-xl font-bold text-[#FFD700] mb-4 flex items-center">
                <FaBullseye className="mr-2" /> Financial Goals
              </h3>
              
              {/* Retirement */}
              <div className="mb-6">
                <h4 className="text-lg font-medium text-white mb-3 flex items-center">
                  <FaUmbrellaBeach className="mr-2 text-orange-400" /> Retirement Planning
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <DataField label="Retirement Age" value={client.retirement_age} />
                  <DataField label="Monthly Expense at Retirement" value={formatCurrency(client.retirement_monthly_expense)} />
                </div>
              </div>

              {/* Child Goals */}
              {client.child_goals && client.child_goals.length > 0 && (
                <div className="mb-6 pt-4 border-t border-[#2D5A8F]">
                  <h4 className="text-lg font-medium text-white mb-3 flex items-center">
                    <FaChild className="mr-2 text-blue-400" /> Child Goals
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {client.child_goals.map((goal, idx) => (
                      <div key={idx} className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F]">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-white font-medium">{goal.child_name || `Child ${idx + 1}`}</p>
                          <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                            {goal.goal_type === 'education' ? 'Education' : goal.goal_type === 'marriage' ? 'Marriage' : 'Both'}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <p className="text-gray-400">Target: <span className="text-green-400">{formatCurrency(goal.target_amount)}</span></p>
                          <p className="text-gray-400">Year: <span className="text-white">{goal.target_year}</span></p>
                          <p className="text-gray-400">Saved: <span className="text-[#FFD700]">{formatCurrency(goal.current_savings)}</span></p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Standard Goals */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <DataField label="Home Purchase Goal" value={formatCurrency(client.home_purchase_goal)} icon={<FaHome className="text-yellow-400" />} />
                <DataField label="Vacation Fund" value={formatCurrency(client.vacation_fund)} icon={<FaGlobe className="text-green-400" />} />
                <DataField label="Emergency Fund Target" value={formatCurrency(client.emergency_fund_target)} icon={<FaShieldAlt className="text-red-400" />} />
              </div>

              {/* Other Goals */}
              {client.other_goals && client.other_goals.length > 0 && (
                <div className="pt-4 border-t border-[#2D5A8F]">
                  <h4 className="text-lg font-medium text-white mb-3 flex items-center">
                    <FaBullseye className="mr-2 text-purple-400" /> Other Financial Goals
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {client.other_goals.map((goal, idx) => (
                      <div key={idx} className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F]">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-white font-medium">{goal.goal_name}</p>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            goal.priority === 'high' ? 'bg-red-500/20 text-red-400' :
                            goal.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {goal.priority} priority
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <p className="text-gray-400">Target: <span className="text-green-400">{formatCurrency(goal.target_amount)}</span></p>
                          <p className="text-gray-400">Year: <span className="text-white">{goal.target_year}</span></p>
                          <p className="text-gray-400">Saved: <span className="text-[#FFD700]">{formatCurrency(goal.current_savings)}</span></p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Risk Profile */}
              <div className="mt-6 pt-4 border-t border-[#2D5A8F]">
                <h4 className="text-lg font-medium text-white mb-3">Risk Profile</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <DataField label="Risk Tolerance" value={client.risk_tolerance} />
                  <DataField label="Investment Experience" value={client.investment_experience} />
                  <DataField label="Investment Horizon" value={client.investment_horizon} />
                </div>
              </div>
            </div>

            {/* Action Notice */}
            <div className="bg-gradient-to-r from-blue-900/30 to-blue-800/30 rounded-xl p-4 border border-blue-500/50">
              <p className="text-blue-400 text-sm">
                <strong>Next Step:</strong> Use the "Edit Arth Gurukul" button above to fill in the complete financial planning data sheet based on this client information. Then discuss with the client and approve report access.
              </p>
            </div>
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="space-y-6">
            {/* Personal Information */}
            <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
              <h3 className="text-xl font-bold text-[#FFD700] mb-4">Personal Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-400 text-sm">Full Name</p>
                  <p className="text-white font-medium">{client.name}</p>
                </div>
                {client.profile_occupation && (
                  <div>
                    <p className="text-gray-400 text-sm">Occupation</p>
                    <p className="text-white font-medium">{client.profile_occupation}</p>
                  </div>
                )}
                {client.age && (
                  <div>
                    <p className="text-gray-400 text-sm">Age</p>
                    <p className="text-white font-medium">{client.age} years</p>
                  </div>
                )}
                {client.contact && (
                  <div>
                    <p className="text-gray-400 text-sm">Contact</p>
                    <p className="text-white font-medium">{client.contact}</p>
                  </div>
                )}
                {client.spouse_name && (
                  <div>
                    <p className="text-gray-400 text-sm">Spouse</p>
                    <p className="text-white font-medium">{client.spouse_name}</p>
                  </div>
                )}
                <div>
                  <p className="text-gray-400 text-sm">Total Dependents</p>
                  <p className="text-white font-medium">{client.total_dependents}</p>
                </div>
              </div>
            </div>

            {/* Children */}
            {client.children && client.children.length > 0 && (
              <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
                <h3 className="text-xl font-bold text-[#FFD700] mb-4">Children</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {client.children.map((child, index) => (
                    <div key={index} className="bg-[#0A1929] rounded-lg p-4 border border-[#2D5A8F]">
                      <p className="text-white font-semibold mb-2">{child.name}</p>
                      <div className="space-y-1 text-sm">
                        {child.age && (
                          <p className="text-gray-400">Age: <span className="text-white">{child.age} years</span></p>
                        )}
                        {child.class_grade && (
                          <p className="text-gray-400">Class: <span className="text-white">{child.class_grade}</span></p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'risk' && <RiskProfiler clientId={id} />}

        {activeTab === 'investments' && <InvestmentTracker clientId={id} />}

        {activeTab === 'liabilities' && <LiabilityTracker clientId={id} />}

        {activeTab === 'insurance' && <InsuranceAnalysis clientId={id} />}

        {activeTab === 'goals' && <GoalPlanning clientId={id} client={client} />}

        {activeTab === 'networth' && <NetWorthSnapshot clientId={id} />}

        {activeTab === 'summary' && <ExecutiveSummary clientId={id} />}

        {activeTab === 'wisdom' && <PersonalFinanceWisdom />}

        {activeTab === 'nextsteps' && <NextStepsFeedback clientId={id} advisorId={client?.advisor_id} />}
      </div>

      {/* PDF Report Preview Modal */}
      {showPDFPreview && (
        <PDFReportPreview 
          clientId={id} 
          onClose={() => setShowPDFPreview(false)} 
        />
      )}
      
      {/* Finalize Report Modal */}
      {showFinalizeModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="finalize-modal">
          <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-[#2D5A8F]">
            {/* Modal Header */}
            <div className="p-6 border-b border-[#2D5A8F]">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <FaCheckCircle className="text-purple-400 text-xl" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Finalize Report</h2>
                    <p className="text-gray-400 text-sm">Complete the planning and share with {client.name}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowFinalizeModal(false)}
                  className="p-2 hover:bg-[#0A1929] rounded-lg transition-all"
                >
                  <FaTimes className="text-gray-400" />
                </button>
              </div>
            </div>
            
            {/* Report Summary */}
            <div className="p-6 space-y-6">
              {/* Financial Overview */}
              <div>
                <h3 className="text-[#FFD700] font-bold mb-3 flex items-center">
                  <FaWallet className="mr-2" /> Financial Overview
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-[#0A1929] rounded-lg p-3">
                    <p className="text-gray-400 text-xs">Net Worth</p>
                    <p className="text-[#FFD700] font-bold text-lg">{formatCurrency(totalAssets - totalLiabilities)}</p>
                  </div>
                  <div className="bg-[#0A1929] rounded-lg p-3">
                    <p className="text-gray-400 text-xs">Total Investments</p>
                    <p className="text-green-400 font-bold text-lg">{formatCurrency(totalAssets)}</p>
                  </div>
                  <div className="bg-[#0A1929] rounded-lg p-3">
                    <p className="text-gray-400 text-xs">Total Liabilities</p>
                    <p className="text-red-400 font-bold text-lg">{formatCurrency(totalLiabilities)}</p>
                  </div>
                </div>
              </div>
              
              {/* Insurance Gaps */}
              <div>
                <h3 className="text-[#FFD700] font-bold mb-3 flex items-center">
                  <FaShieldAlt className="mr-2" /> Insurance Gaps (Auto-calculated)
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-[#0A1929] rounded-lg p-3 border border-red-500/30">
                    <p className="text-gray-400 text-xs">Life Insurance Gap</p>
                    <p className="text-red-400 font-bold">
                      {formatCurrency(Math.max(0, ((parseInt(client.annual_income) || 0) * 10) - totalInsuranceCover))}
                    </p>
                    <p className="text-gray-500 text-xs mt-1">Required: {formatCurrency((parseInt(client.annual_income) || 0) * 10)}</p>
                  </div>
                  <div className="bg-[#0A1929] rounded-lg p-3 border border-pink-500/30">
                    <p className="text-gray-400 text-xs">Health Insurance Gap</p>
                    <p className="text-pink-400 font-bold">
                      {formatCurrency(Math.max(0, ((parseInt(client.annual_income) || 0) * 2) - (parseInt(client.health_insurance_cover) || 0)))}
                    </p>
                    <p className="text-gray-500 text-xs mt-1">Required: {formatCurrency((parseInt(client.annual_income) || 0) * 2)}</p>
                  </div>
                </div>
              </div>
              
              {/* Goals Summary */}
              {((client.child_goals?.length > 0) || (client.other_goals?.length > 0)) && (
                <div>
                  <h3 className="text-[#FFD700] font-bold mb-3 flex items-center">
                    <FaPiggyBank className="mr-2" /> Goals Summary
                  </h3>
                  <div className="bg-[#0A1929] rounded-lg p-3 max-h-32 overflow-y-auto">
                    <div className="space-y-2">
                      {client.child_goals?.map((g, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <span className="text-gray-400">{g.child_name}'s {g.goal_type}</span>
                          <span className="text-white">{formatCurrency(g.target_amount)}</span>
                        </div>
                      ))}
                      {client.other_goals?.map((g, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <span className="text-gray-400">{g.goal_name}</span>
                          <span className="text-white">{formatCurrency(g.target_amount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Next Review Date */}
              <div>
                <h3 className="text-[#FFD700] font-bold mb-3 flex items-center">
                  <FaCalendar className="mr-2" /> Next Review Date
                </h3>
                <input
                  type="date"
                  value={finalizeData.next_review_date}
                  onChange={(e) => setFinalizeData(prev => ({ ...prev, next_review_date: e.target.value }))}
                  className="w-full bg-[#0A1929] border border-[#2D5A8F] rounded-lg px-4 py-3 text-white focus:border-[#FFD700] focus:outline-none"
                  min={new Date().toISOString().split('T')[0]}
                  data-testid="next-review-date-input"
                />
              </div>
              
              {/* Key Action Items */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-[#FFD700] font-bold flex items-center">
                    <FaClipboardList className="mr-2" /> Key Action Items
                  </h3>
                  <button
                    onClick={addActionItem}
                    className="text-sm text-[#FFD700] hover:text-[#D4AF37] flex items-center"
                    type="button"
                  >
                    <FaPlus className="mr-1" /> Add Item
                  </button>
                </div>
                <div className="space-y-2">
                  {finalizeData.key_action_items.map((item, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <span className="flex-shrink-0 w-6 h-6 bg-[#FFD700] text-[#0A1929] rounded-full flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </span>
                      <input
                        type="text"
                        value={item}
                        onChange={(e) => updateActionItem(index, e.target.value)}
                        placeholder="e.g., Increase term insurance coverage by ₹50L"
                        className="flex-1 bg-[#0A1929] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white text-sm focus:border-[#FFD700] focus:outline-none"
                        data-testid={`action-item-${index}`}
                      />
                      {finalizeData.key_action_items.length > 1 && (
                        <button
                          onClick={() => removeActionItem(index)}
                          className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"
                          type="button"
                        >
                          <FaTimes size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Advisor Notes */}
              <div>
                <h3 className="text-[#FFD700] font-bold mb-3 flex items-center">
                  <FaEdit className="mr-2" /> Notes for Client
                </h3>
                <textarea
                  value={finalizeData.advisor_notes}
                  onChange={(e) => setFinalizeData(prev => ({ ...prev, advisor_notes: e.target.value }))}
                  placeholder="Add any personalized notes or recommendations for the client..."
                  rows={3}
                  className="w-full bg-[#0A1929] border border-[#2D5A8F] rounded-lg px-4 py-3 text-white focus:border-[#FFD700] focus:outline-none resize-none"
                  data-testid="advisor-notes-input"
                />
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="p-6 border-t border-[#2D5A8F] flex items-center justify-end space-x-4">
              <button
                onClick={() => setShowFinalizeModal(false)}
                className="px-6 py-2 text-gray-400 hover:text-white transition-all"
                disabled={finalizing}
              >
                Cancel
              </button>
              <button
                onClick={handleFinalizeReport}
                disabled={finalizing}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-500 text-white font-bold rounded-lg hover:from-purple-500 hover:to-purple-400 transition-all flex items-center space-x-2 disabled:opacity-50"
                data-testid="confirm-finalize-btn"
              >
                {finalizing ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    <span>Finalizing...</span>
                  </>
                ) : (
                  <>
                    <FaCheckCircle />
                    <span>Finalize & Notify Client</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper Components for Client Data Display
const DataField = ({ label, value, highlight, icon, span = 1 }) => {
  if (!value && value !== 0) return null;
  
  const highlightClass = highlight === 'green' ? 'text-green-400' : 
                         highlight === 'red' ? 'text-red-400' : 'text-white';
  
  return (
    <div className={span === 2 ? 'md:col-span-2' : ''}>
      <p className="text-gray-400 text-sm flex items-center">
        {icon && <span className="mr-2">{icon}</span>}
        {label}
      </p>
      <p className={`font-medium ${highlightClass}`}>{value}</p>
    </div>
  );
};

const LoanRow = ({ icon: Icon, label, outstanding, emi, isProtected, formatCurrency }) => {
  if (!outstanding && !emi) return null;
  
  return (
    <div className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F]">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Icon className="text-[#FFD700]" />
          <span className="text-white font-medium">{label}</span>
          {isProtected !== undefined && (
            <span className={`px-2 py-0.5 text-xs rounded-full flex items-center ${
              isProtected ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
            }`}>
              {isProtected ? <><FaCheckCircle className="mr-1" /> Protected</> : <><FaTimesCircle className="mr-1" /> Unprotected</>}
            </span>
          )}
        </div>
        <div className="text-right">
          <p className="text-red-400 font-bold">{formatCurrency(outstanding)}</p>
          {emi && <p className="text-gray-400 text-sm">EMI: {formatCurrency(emi)}/mo</p>}
        </div>
      </div>
    </div>
  );
};

const InsuranceCard = ({ label, cover, premium, formatCurrency, color }) => {
  const colorClasses = {
    red: 'border-red-500/50 from-red-900/20 to-red-800/20',
    green: 'border-green-500/50 from-green-900/20 to-green-800/20',
    blue: 'border-blue-500/50 from-blue-900/20 to-blue-800/20'
  };
  
  const textColor = {
    red: 'text-red-400',
    green: 'text-green-400',
    blue: 'text-blue-400'
  };

  return (
    <div className={`bg-gradient-to-br ${colorClasses[color]} rounded-lg p-4 border`}>
      <p className={`font-medium ${textColor[color]}`}>{label}</p>
      <p className="text-2xl font-bold text-white mt-1">{formatCurrency(cover)}</p>
      {premium && <p className="text-gray-400 text-sm mt-1">Premium: {formatCurrency(premium)}/year</p>}
    </div>
  );
};

export default ClientPlanView;
